import React, { useState } from 'react';
import { StatCard } from '../components/StatCard';
import { BouncingDots } from '../../../shared/components/BouncingDots';
import { Pagination } from '../../../shared/components/Pagination';
import { RequestCard } from '../components/RequestCard';
import { useDashboardData } from '../hooks/useDashboardData';
import { ConfirmOperationModal } from '../components/ConfirmOperationModal';
import { getCurrentSessionToken } from '../../auth';
import { approveOperation, rejectOperation } from '../services/operationService';
import { formatCurrency } from '../../../shared/utils';

export const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'today' | 'deferred'>('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isProcessing, setIsProcessing] = useState(false);
  const [reasonInput, setReasonInput] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;

  const { requests, activeProducers, stats, isLoading, reload } = useDashboardData(filterType);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'danger';
    title: string;
    description: React.ReactNode;
    requestId: string | number | null;
    action: 'approve' | 'reject' | null;
    amount: number;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    description: '',
    requestId: null,
    action: null,
    amount: 0
  });

  const handleApprove = (id: string | number) => {
    const request = requests[activeTab].find(r => r.id === id);
    setConfirmModal({
      isOpen: true,
      type: 'success',
      title: 'Aprobar Solicitud',
      description: <>¿Estás seguro que deseas aprobar la solicitud de <strong className="text-base text-gray-900">{request?.producer}</strong>?</>,
      requestId: id,
      action: 'approve',
      amount: request?.amount || 0
    });
  };

  const handleReject = (id: string | number) => {
    const request = requests[activeTab].find(r => r.id === id);
    setReasonInput('');
    setConfirmModal({
      isOpen: true,
      type: 'danger',
      title: 'Rechazar Solicitud',
      description: <>¿Estás seguro que deseas rechazar la solicitud de <strong className="text-base text-gray-900">{request?.producer}</strong>?</>,
      requestId: id,
      action: 'reject',
      amount: request?.amount || 0
    });
  };

  const executeAction = async () => {
    if (!confirmModal.requestId || !confirmModal.action) return;

    try {
      setIsProcessing(true);
      const token = await getCurrentSessionToken();
      if (!token) throw new Error("No active session token found");

      const opId = String(confirmModal.requestId);

      if (confirmModal.action === 'approve') {
        await approveOperation(opId, token);
      } else if (confirmModal.action === 'reject') {
        if (!reasonInput.trim()) {
          alert("Por favor, ingresá un motivo de rechazo.");
          setIsProcessing(false);
          return;
        }
        await rejectOperation(opId, reasonInput.trim(), token);
      }

      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      await reload();
    } catch (err: any) {
      console.error("Failed to execute operation action:", err);
      alert(err.message || "Ocurrió un error al procesar la operación.");
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredRequests = requests[activeTab].filter(req => {
    const matchesSearch = req.producer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || req.type === filterType;
    return matchesSearch && matchesType;
  });

  const otherTab = activeTab === 'today' ? 'deferred' : 'today';
  const otherTabMatches = requests[otherTab].filter(req => {
    const matchesSearch = req.producer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || req.type === filterType;
    return matchesSearch && matchesType;
  }).length;

  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filterType, searchTerm]);

  const paginatedRequests = filteredRequests.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#212121]">Gestión de Solicitudes</h1>
          <p className="text-muted-foreground mt-1">
            Bandeja de entrada central para procesar solicitudes de productores
          </p>
        </div> 
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input 
            type="text" 
            placeholder="Buscar por nombre de productor..."
            className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-sm text-[#212121]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="w-full sm:w-[200px] px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-sm font-medium text-[#212121]"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">Todos los tipos</option>
          <option value="e-cheque">E-Cheque</option>
          <option value="transferencia">Transferencia</option>
        </select>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          label="Solicitudes pendientes" 
          value={isLoading ? '...' : (stats ? String(stats.pendientes) : String(requests.today.length + requests.deferred.length))} 
          icon="pending" 
        />
        <StatCard 
          label="Solicitudes aprobadas" 
          value={isLoading ? '...' : (stats ? String(stats.aprobadas) : '0')} 
          icon="approved" 
        />
        <StatCard 
          label="Productores activos" 
          value={isLoading ? '...' : String(activeProducers)} 
          icon="producers" 
        />
        <StatCard 
          label="Volumen operado hoy" 
          value={isLoading ? '...' : (stats ? formatCurrency(stats.volumen_operado) : '$0')} 
          icon="volume" 
          gradient 
        />
      </div>

      <div className="space-y-4">
        <div className="inline-flex bg-muted rounded-lg p-1">
          <button 
            onClick={() => setActiveTab('today')}
            className={`px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2 transition ${
              activeTab === 'today' ? 'bg-background shadow-sm text-foreground active' : 'text-muted-foreground hover:bg-background/50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Operaciones Pendientes
            <span className="ml-1 text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5 font-bold">
              {requests.today.length}
            </span>
          </button>
          <button 
            onClick={() => setActiveTab('deferred')}
            className={`px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2 transition ${
              activeTab === 'deferred' ? 'bg-background shadow-sm text-foreground active' : 'text-muted-foreground hover:bg-background/50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z"/>
            </svg>
            Operaciones diferidas
            <span className="ml-1 text-xs bg-amber-500 text-white rounded-full px-2 py-0.5 font-bold">
              {requests.deferred.length}
            </span>
          </button>
        </div>
        
        <p className="text-sm text-muted-foreground">
          {activeTab === 'today' 
            ? `Solicitudes recibidas antes del corte diario (${stats?.cutoff_hora_arg || '11:00 AM ART'}) que se liquidan hoy.` 
            : `Solicitudes recibidas después del corte diario que quedan diferidas para el próximo día hábil.`}
        </p>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <BouncingDots />
            <p className="text-muted-foreground mt-4">Cargando solicitudes pendientes...</p>
          </div>
        ) : filteredRequests.length > 0 ? (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {paginatedRequests.map(req => (
                <RequestCard 
                  key={req.id} 
                  request={req} 
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
            </div>
            <div className="bg-white rounded-lg border border-border">
              <Pagination 
                currentPage={currentPage} 
                totalPages={Math.ceil(filteredRequests.length / ITEMS_PER_PAGE)} 
                onPageChange={setCurrentPage} 
                itemsLabel={`Mostrando ${(currentPage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(currentPage * ITEMS_PER_PAGE, filteredRequests.length)} de ${filteredRequests.length}`}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-gray-200 rounded-2xl bg-gray-50/30">
            <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mb-4 border border-gray-100">
              <svg className="w-8 h-8 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            {searchTerm.trim() ? (
              <>
                <p className="text-muted-foreground font-medium">
                  Sin resultados para{' '}
                  <span className="font-bold text-[#212121]">"{searchTerm.trim()}"</span>
                </p>
                {otherTabMatches > 0 && (
                  <button
                    onClick={() => setActiveTab(otherTab)}
                    className="mt-3 text-sm font-semibold text-primary hover:underline"
                  >
                    Ver {otherTabMatches} resultado{otherTabMatches > 1 ? 's' : ''} en {otherTab === 'today' ? 'Operaciones Pendientes' : 'Operaciones diferidas'} →
                  </button>
                )}
              </>
            ) : (
              <p className="text-muted-foreground font-medium">No hay solicitudes para mostrar</p>
            )}
          </div>
        )}
      </div>

      <ConfirmOperationModal
        isOpen={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        description={confirmModal.description}
        amount={confirmModal.amount}
        action={confirmModal.action}
        reasonInput={reasonInput}
        setReasonInput={setReasonInput}
        isProcessing={isProcessing}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onExecute={executeAction}
      />
    </div>
  );
};
